import { defineDocument, strong, text } from '../../schema';
import { POPPY_CHAT_COMMAND_NAMESPACE, POPPY_CHAT_COMMAND_COOLDOWN_MS, getPoppyChatCommandRows, } from '../../../configs/poppyChatCommands';
import { twitchStaticConfig } from '../../../configs/twitch';
const poppyChatCommandRows = getPoppyChatCommandRows(true).map(({ name, description }) => [[strong(name)], [text(description)]]);
export const featureModeratorToolsDocument = defineDocument({
    id: 'feature:moderator-tools',
    kind: 'feature',
    slug: 'moderator-tools',
    route: '/discover/features/moderator-tools',
    seo: {
        title: 'Discover Moderator Tools',
        description: 'Moderation tools for streamers and hosts. Control player access, filter profanity, and let Poppy engage with your Twitch chat.',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Discover Feature',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Moderator Tools',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Moderation tools for streamers and hosts. Control player access, filter profanity, and let Poppy engage with your Twitch chat.',
            },
        ],
    },
    aside: {
        showToc: true,
    },
    sections: [
        {
            id: 'overview',
            title: [
                {
                    type: 'text',
                    text: 'Your Moderation Toolkit',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Moderator Tools help hosts manage their rooms and their chat. Control who can join, filter profanity in player inputs, and let Poppy help engage with your channel. These tools help streamers create safe, enjoyable environments for their communities.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Access and update your Moderator Tools anytime from the ',
                        },
                        {
                            type: 'link',
                            href: '/library?section=player-access',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Library',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' sidebar, under Moderation. Changes save automatically.',
                        },
                    ],
                },
                {
                    type: 'image',
                    imageAsset: 'featureModToolsSidebar',
                    imageAlt: 'Moderation group in the Library sidebar showing Player Access, Censorship, and Poppy in Twitch Chat',
                    caption: [
                        {
                            type: 'text',
                            text: 'Moderator Tools are configurable from the Library sidebar.',
                        },
                    ],
                    maxWidth: 400,
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Player Access and Censorship settings apply to rooms when you create them. Once a room is created, these settings are locked for that room. To use different settings, update them in the Library and then create a new room. Poppy in Twitch Chat settings take effect right away.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Moderator Tools work alongside your ',
                        },
                        {
                            type: 'link',
                            href: '/discover/features/user-preferences',
                            children: [
                                {
                                    type: 'text',
                                    text: 'User Preferences',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: '. While User Preferences define the style and content of your game experience, Moderator Tools help to control how players access and contribute to your games.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'player-access',
            title: [
                {
                    type: 'text',
                    text: 'Player Access',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Player Access settings help users monitor and control who can join their game rooms.',
                        },
                    ],
                },
                {
                    type: 'image',
                    imageAsset: 'featurePlayerAccessMenu',
                    imageAlt: 'Player Access menu in Moderator Tools sidebar',
                    caption: [
                        {
                            type: 'text',
                            text: 'Player Access menu in Moderator Tools sidebar.',
                        },
                    ],
                    maxWidth: 400,
                },
            ],
            sections: [
                {
                    id: 'player-access__require-twitch-login',
                    title: [
                        {
                            type: 'text',
                            text: 'Require Twitch Login',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'When enabled, ',
                                },
                                {
                                    type: 'strong',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Require Twitch Login',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' will prompt all players to authenticate with Twitch before joining your room. This setting is ideal for streamers who want to hold players accountable for their behavior.',
                                },
                            ],
                        },
                        {
                            type: 'image',
                            imageAsset: 'featurePlayerTwitch',
                            imageAlt: 'Player join screens with Twitch authentication requirement',
                            caption: [
                                {
                                    type: 'text',
                                    text: 'Player screens before and after Twitch authentication.',
                                },
                            ],
                            maxWidth: 560,
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Players will see a ',
                                },
                                {
                                    type: 'strong',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Join with Twitch',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: " button prior to being able to confirm their player for the room. After authenticating, the player's Twitch username is automatically used as their display name. The Twitch name is not subject to the character limits and validation rules as described in ",
                                },
                                {
                                    type: 'link',
                                    href: '/discover/features/game-room-lifecycle#joining-a-room',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'joining a room',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ". Players can still customize their avatar's color and facial expression when using Twitch login.",
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'player-access__disallow-blocked-banned',
                    title: [
                        {
                            type: 'text',
                            text: 'Disallow Blocked & Banned',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Disallow Blocked & Banned stops Twitch users you have blocked, or banned from your channel, from joining your Airhead Games rooms. ',
                                },
                                {
                                    type: 'strong',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Require Twitch Login',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' must be on for this setting to work, because Airhead Games checks each player by their Twitch account.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Turn on ',
                                },
                                {
                                    type: 'strong',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Disallow Blocked & Banned',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' in the ',
                                },
                                {
                                    type: 'link',
                                    href: '/library?section=player-access',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Player Access',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' section of the Library sidebar. The first time, Twitch asks you to let Airhead Games read your blocked users and your channel bans. Airhead Games then copies both lists. Anyone on either list is denied access when they try to join.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'To see who is on the synced list, open ',
                                },
                                {
                                    type: 'link',
                                    href: '/library?section=player-access&dialog=blocked-users',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Blocked users',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: '. To refresh the lists now, turn Disallow Blocked & Banned off and then on again. Learn how Airhead Games ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/features/twitch#twitch-permissions',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'uses your Twitch permissions',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: '.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'player-access__remove-player',
                    title: [
                        {
                            type: 'text',
                            text: 'Remove Player',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Hosts can remove individual players from the room at any time, whether in the lobby or during an in-progress game. No special settings are required.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Open ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/features/poppy#in-game-menu',
                                    children: [
                                        {
                                            type: 'text',
                                            text: "Poppy's Menu",
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ', select the player, confirm, and they are removed. The room must stay above the minimum player count for the game if the game is already in progress. After removing someone, in-progress games automatically adapt to the new number of players.',
                                },
                            ],
                        },
                        {
                            type: 'image',
                            imageAsset: 'featureRemovePlayer',
                            imageAlt: 'Remove Player options in Poppy menu',
                            caption: [
                                {
                                    type: 'text',
                                    text: 'Remove Player options in Poppy menu.',
                                },
                            ],
                            maxWidth: 400,
                        },
                    ],
                },
            ],
        },
        {
            id: 'censorship',
            title: [
                {
                    type: 'text',
                    text: 'Censorship',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Censorship settings help hosts filter profanity in player inputs, including player names during the join process and player-generated content during gameplay. The only exception: when ',
                        },
                        {
                            type: 'strong',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Require Twitch Login',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' is enabled, Twitch usernames are not subject to profanity checks. Our censorship tools complement the ',
                        },
                        {
                            type: 'link',
                            href: '/discover/features/user-preferences#content-style',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Content Style',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' preference, which controls Poppy-generated content.',
                        },
                    ],
                },
                {
                    type: 'image',
                    imageAsset: 'featureCensorshipMenu',
                    imageAlt: 'Censorship menu in Moderator Tools sidebar',
                    caption: [
                        {
                            type: 'text',
                            text: 'Censorship menu in Moderator Tools sidebar.',
                        },
                    ],
                    maxWidth: 400,
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'These tools are built to help you run a cleaner room with less effort. They can check player inputs against a built-in profanity dictionary, your custom word list, public Twitch blocked terms, or a combination of those sources. Common and Custom Profanity tools can be used in tandem or separately.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'When a player submits text that matches an enabled filter, the input is disallowed and the player is asked to enter something else.',
                        },
                    ],
                },
                {
                    type: 'callout',
                    tone: 'warning',
                    content: [
                        {
                            type: 'strong',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Important note: ',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: 'Censorship only checks for profanity. It cannot protect against every player input that may be generally offensive, disruptive, or inappropriate but is not considered profane.',
                        },
                    ],
                },
            ],
            sections: [
                {
                    id: 'censorship__common-profanity',
                    title: [
                        {
                            type: 'text',
                            text: 'Common Profanity',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Enable Common Profanity to block words from the built-in profanity dictionary. Our dictionary includes words and phrases from all of the most frequently used languages in the world.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'censorship__custom-profanity',
                    title: [
                        {
                            type: 'text',
                            text: 'Custom Profanity',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Enable Custom Profanity to block words from your own curated list, or from public terms you blocked on Twitch. See ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/features/moderator-tools#censorship__blocked-on-twitch',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Blocked on Twitch',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' for details on importing Twitch terms.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'censorship__custom-words',
                    title: [
                        {
                            type: 'text',
                            text: 'Custom Words',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Adding and removing words in your custom profanity list gives you additional control over what players can submit in your games.',
                                },
                            ],
                        },
                        {
                            type: 'image',
                            imageAsset: 'featureCustomProfanity',
                            imageAlt: 'Custom profanity management interface',
                            caption: [
                                {
                                    type: 'text',
                                    text: 'Add and remove custom words to be blocked from player inputs.',
                                },
                            ],
                            maxWidth: 600,
                        },
                    ],
                },
                {
                    id: 'censorship__blocked-on-twitch',
                    title: [
                        {
                            type: 'text',
                            text: 'Blocked on Twitch',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'With Custom Profanity on and your Twitch account linked, open ',
                                },
                                {
                                    type: 'link',
                                    href: '/library?section=censorship&dialog=blocked-twitch-terms',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Blocked on Twitch',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' and turn on the sync switch. The first time, Twitch asks you to let Airhead Games read your blocked terms. Your public blocked terms are then added to the profanity filter for rooms you create.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Twitch does not share terms you marked private. To filter those here, add them to Custom Words. To refresh the terms now, turn Blocked on Twitch off and then on again. Learn how Airhead Games ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/features/twitch#twitch-permissions',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'uses your Twitch permissions',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: '.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'censorship__rigid-mode',
                    title: [
                        {
                            type: 'text',
                            text: 'Rigid Mode',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Enable Rigid Mode to catch profanity hidden inside other words. It is stricter than the standard filters and can be used when you want a more conservative approach to player content moderation.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Please be aware that enabling this mode may block inputs you would not expect to be censored. For example, it will block the word "password" because it contains the word "ass" within it. Some players may find this confusing or frustrating during gameplay, but we want to give you the flexibility to moderate your content as you see fit.',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'poppy-in-twitch-chat',
            title: [
                {
                    type: 'text',
                    text: 'Poppy in Twitch Chat',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Poppy can make hosting easier by automatically sharing join links and responding to commands in your Twitch chat.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Open ',
                        },
                        {
                            type: 'link',
                            href: '/library?section=poppy-chat',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Poppy in Twitch Chat',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' in the Library sidebar, connect Twitch if prompted, and turn on the features you want. Each feature works independently.',
                        },
                    ],
                },
                {
                    type: 'image',
                    imageAsset: 'featurePitcMenu',
                    imageAlt: 'Poppy in Twitch Chat menu with Auto-post room join link and Respond to commands switches',
                    caption: [
                        {
                            type: 'text',
                            text: 'Poppy in Twitch Chat settings in the Library sidebar.',
                        },
                    ],
                    maxWidth: 400,
                },
            ],
            sections: [
                {
                    id: 'poppy-in-twitch-chat__auto-post-join-link',
                    title: [
                        {
                            type: 'text',
                            text: 'Auto-Post Room Join Link',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Poppy posts the join link in your chat each time you open a new game room.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'poppy-in-twitch-chat__respond-to-commands',
                    title: [
                        {
                            type: 'text',
                            text: 'Respond to Commands',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'When this setting is on, anyone in your chat can type ',
                                },
                                {
                                    type: 'strong',
                                    children: [
                                        {
                                            type: 'text',
                                            text: `${POPPY_CHAT_COMMAND_NAMESPACE} [command]`,
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' and Poppy replies.',
                                },
                            ],
                        },
                        {
                            type: 'table',
                            columns: [
                                [{ type: 'text', text: 'Command' }],
                                [{ type: 'text', text: 'Info' }],
                            ],
                            rows: poppyChatCommandRows,
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'How Poppy handles commands:',
                                },
                            ],
                        },
                        {
                            type: 'list',
                            items: [
                                [
                                    {
                                        type: 'text',
                                        text: 'Commands are not case-sensitive.',
                                    },
                                ],
                                [
                                    {
                                        type: 'text',
                                        text: 'Poppy ignores unknown commands.',
                                    },
                                ],
                                [
                                    {
                                        type: 'text',
                                        text: 'Commands work even if you have no room open. When someone asks about a room or game and none is open, Poppy says so.',
                                    },
                                ],
                                [
                                    {
                                        type: 'text',
                                        text: 'If you have more than one room open, Poppy answers for the room you opened most recently.',
                                    },
                                ],
                                [
                                    {
                                        type: 'text',
                                        text: `Poppy answers each command once every ${POPPY_CHAT_COMMAND_COOLDOWN_MS / 1000} seconds. Commands with the same info share this limit.`,
                                    },
                                ],
                            ],
                        },
                    ],
                },
                {
                    id: 'poppy-in-twitch-chat__blocked-links',
                    title: [
                        {
                            type: 'text',
                            text: 'Blocked Links',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "If your chat blocks links, through Twitch's Block Hyperlinks setting or a chat bot like StreamElements or Nightbot, we suggest you make Poppy a mod to get the most out of these features. When a message is removed from chat because it included a link, Poppy will flag your attention in the room and your chat with the suggestion to add them as a mod.",
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'As a mod, Poppy will never take any actions you did not explicitly enable in your Airhead Games settings. Allow Poppy to post links by typing ',
                                },
                                {
                                    type: 'strong',
                                    children: [
                                        {
                                            type: 'text',
                                            text: `/mod ${twitchStaticConfig.botLogin}`,
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' in your chat. Then try typing your command again. For example, type ',
                                },
                                {
                                    type: 'strong',
                                    children: [
                                        {
                                            type: 'text',
                                            text: `${POPPY_CHAT_COMMAND_NAMESPACE} join`,
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' to post the join link for a game room.',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
    ],
});
//# sourceMappingURL=moderator-tools.js.map